import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";

const FACINGS = ["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const SERVICES = ["Architecture", "Interior", "Construction", "Complete Turnkey", "Renovation"];
const TIMELINES = ["Immediately", "Within 1 Month", "1–3 Months", "3–6 Months", "Just Exploring"];

const getUTM = () => {
  const p = new URLSearchParams(window.location.search);
  return { utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "", utm_campaign: p.get("utm_campaign") || "" };
};

const input = "w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] outline-none focus:border-gold";
const label = "block text-sm font-semibold text-ink mb-1.5";

export default function PlanWizard({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [f, setF] = useState({ plot_width: "", plot_depth: "", city: "", facing: "", floors: "", bhk: "", service: "", budget: "", timeline: "", name: "", phone: "", whatsapp: "", email: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const reset = () => { setStep(0); setStatus("idle"); setError(""); };
  const close = () => { onClose(); setTimeout(reset, 300); };

  const submit = async () => {
    if (!f.name.trim() || f.phone.trim().length < 7) { setError("Please enter your name and a valid mobile number."); return; }
    setStatus("loading"); setError("");
    try {
      await api.post("/leads", {
        ...f, ...getUTM(),
        plot_size: f.plot_width && f.plot_depth ? `${f.plot_width}x${f.plot_depth}` : "",
        source_page: "Plan Your Dream Home",
      });
      setStatus("done");
    } catch (e) { setError(formatError(e.response?.data?.detail)); setStatus("idle"); }
  };

  const steps = [
    { title: "Your Plot", body: (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>Plot Width (ft)</label><input className={input} type="number" inputMode="numeric" value={f.plot_width} onChange={set("plot_width")} data-testid="plan-width" /></div>
          <div><label className={label}>Plot Depth (ft)</label><input className={input} type="number" inputMode="numeric" value={f.plot_depth} onChange={set("plot_depth")} data-testid="plan-depth" /></div>
        </div>
        <div><label className={label}>Location / City</label><input className={input} value={f.city} onChange={set("city")} placeholder="Indore" data-testid="plan-city" /></div>
      </div>
    )},
    { title: "Configuration", body: (
      <div className="space-y-4">
        <div><label className={label}>Facing</label><select className={input} value={f.facing} onChange={set("facing")} data-testid="plan-facing"><option value="">Select facing</option>{FACINGS.map((x) => <option key={x}>{x}</option>)}</select></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>Floors</label><input className={input} type="number" inputMode="numeric" value={f.floors} onChange={set("floors")} data-testid="plan-floors" /></div>
          <div><label className={label}>BHK</label><input className={input} value={f.bhk} onChange={set("bhk")} placeholder="e.g. 3BHK" data-testid="plan-bhk" /></div>
        </div>
      </div>
    )},
    { title: "Service Required", body: (
      <div className="grid gap-3">
        {SERVICES.map((s) => (
          <label key={s} className={`flex items-center gap-3 rounded-xl border p-4 cursor-pointer ${f.service === s ? "border-gold bg-gold/5" : "border-line"}`}>
            <input type="radio" name="svc" checked={f.service === s} onChange={() => setF({ ...f, service: s })} className="accent-gold" data-testid={`plan-svc-${s}`} />
            <span className="font-semibold text-ink">{s}</span>
          </label>
        ))}
      </div>
    )},
    { title: "Budget & Timeline", body: (
      <div className="space-y-4">
        <div><label className={label}>Approximate Budget</label><input className={input} value={f.budget} onChange={set("budget")} placeholder="e.g. ₹50–70 Lakhs" data-testid="plan-budget" /></div>
        <div><label className={label}>Project Start Timeline</label><select className={input} value={f.timeline} onChange={set("timeline")} data-testid="plan-timeline"><option value="">Select timeline</option>{TIMELINES.map((x) => <option key={x}>{x}</option>)}</select></div>
      </div>
    )},
    { title: "Your Details", body: (
      <div className="space-y-4">
        <div><label className={label}>Name *</label><input className={input} value={f.name} onChange={set("name")} data-testid="plan-name" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={label}>Mobile *</label><input className={input} type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} data-testid="plan-phone" /></div>
          <div><label className={label}>WhatsApp</label><input className={input} type="tel" inputMode="tel" value={f.whatsapp} onChange={set("whatsapp")} data-testid="plan-whatsapp" /></div>
        </div>
        <div><label className={label}>Email (optional)</label><input className={input} type="email" value={f.email} onChange={set("email")} /></div>
      </div>
    )},
  ];

  const last = step === steps.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[95] bg-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}>
          <motion.div data-testid="plan-wizard" className="bg-white w-full sm:max-w-lg rounded-t-xl2 sm:rounded-xl2 max-h-[92vh] overflow-y-auto" initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-white z-10">
              <div><div className="kicker mb-0.5">Plan Your Dream Home</div><h3 className="font-bold text-ink">{status === "done" ? "Done" : steps[step].title}</h3></div>
              <button onClick={close} data-testid="plan-close" className="w-9 h-9 rounded-full hover:bg-sand flex items-center justify-center"><X size={18} /></button>
            </div>

            {status === "done" ? (
              <div className="p-8 text-center">
                <CheckCircle2 size={46} className="mx-auto text-gold mb-3" />
                <h3 className="text-xl font-bold text-ink">Thank you!</h3>
                <p className="text-muted mt-2">Our team will contact you shortly with design options for your dream home.</p>
                <button onClick={close} className="mt-6 rounded-full bg-ink text-white font-semibold px-6 py-3">Close</button>
              </div>
            ) : (
              <div className="p-6">
                <div className="flex gap-1.5 mb-6">
                  {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-gold" : "bg-line"}`} />)}
                </div>
                {steps[step].body}
                {error && <p className="text-sm text-red-500 mt-4" data-testid="plan-error">{error}</p>}
                <div className="flex gap-3 mt-6">
                  {step > 0 && <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 rounded-full border border-line px-5 py-3 font-semibold text-ink"><ArrowLeft size={16} /> Back</button>}
                  {!last ? (
                    <button onClick={() => setStep(step + 1)} data-testid="plan-next" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-ink text-white px-5 py-3 font-semibold">Next <ArrowRight size={16} /></button>
                  ) : (
                    <button onClick={submit} disabled={status === "loading"} data-testid="plan-submit" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-gold text-white px-5 py-3 font-semibold shadow-gold disabled:opacity-70">
                      {status === "loading" ? <><Loader2 size={16} className="animate-spin" /> Submitting...</> : "Get Free Consultation"}
                    </button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
